import {Modal, Select} from '@mantine/core';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {DateInput, TimeInput} from '@mantine/dates';
import React, {useEffect, useMemo, useState} from 'react';
import type {InspectionDispatchInspectorResponse} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {padTwo} from 'src/shared/utils/utcDate';

export interface DispatchScheduleResult {
  assigneeId: number;
  scheduledStartAt: Date;
}

export interface DispatchScheduleDialogProps {
  opened: boolean;
  onClose: () => void;
  title: string;
  inspectors: InspectionDispatchInspectorResponse[];
  initialAssigneeId?: number;
  initialScheduledStartAt?: Date;
  onSubmit: (result: DispatchScheduleResult) => void;
}

export const DispatchScheduleDialog: React.FC<DispatchScheduleDialogProps> = ({
  opened,
  onClose,
  title,
  inspectors,
  initialAssigneeId,
  initialScheduledStartAt,
  onSubmit,
}) => {
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  const [date, setDate] = useState<Date | null>(null);
  const [time, setTime] = useState('08:00');

  useEffect(() => {
    if (!opened) return;
    setAssigneeId(initialAssigneeId ? String(initialAssigneeId) : null);
    const start = initialScheduledStartAt ?? new Date();
    setDate(start);
    setTime(`${padTwo(start.getUTCHours())}:${padTwo(start.getUTCMinutes())}`);
  }, [opened, initialAssigneeId, initialScheduledStartAt]);

  const inspectorOptions = useMemo(
    () =>
      inspectors.map(i => ({
        value: String(i.userId),
        label: `${i.name} (${i.openInspectionCount} open)`,
      })),
    [inspectors]
  );

  const canSubmit = assigneeId && date;

  const handleSubmit = () => {
    if (!assigneeId || !date) return;
    const [h, m] = time.split(':');
    // DateInput yields a local-midnight Date; take its wall-clock Y/M/D and
    // rebuild as a UTC-naive instant so the picked calendar day never shifts.
    const scheduled = new Date(
      Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        Number(h) || 0,
        Number(m) || 0
      )
    );
    onSubmit({assigneeId: Number(assigneeId), scheduledStartAt: scheduled});
  };

  return (
    <Modal opened={opened} onClose={onClose} title={title} size="sm">
      <Stack gap="sm">
        <Select
          label="Inspector"
          placeholder="Select an inspector"
          data={inspectorOptions}
          value={assigneeId}
          onChange={setAssigneeId}
          searchable
          required
        />
        <DateInput
          label="Date"
          placeholder="Scheduled date"
          value={date}
          onChange={setDate}
          valueFormat="MMM D, YYYY"
          required
        />
        <TimeInput
          label="Time"
          value={time}
          onChange={event => setTime(event.currentTarget.value)}
        />
      </Stack>
      <Group justify="start" mt="lg">
        <Button variant="subtle" color="black" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={!canSubmit}>
          Schedule
        </Button>
      </Group>
    </Modal>
  );
};
